/**
 * What Enter moves between in a record form: the places a value is typed or
 * picked. Buttons are left out — the F2, clear and toolbar buttons sit between
 * the fields and Enter is not meant to stop on them.
 */
const FIELD_SELECTOR = [
    'input:not([type="hidden"])',
    'textarea',
    'select',
    // A MUI Select: its native input is out of the tab order, this is the box.
    '.MuiSelect-select[role="combobox"]',
    // A date/time field of MUI X: the typed sections, not a plain input.
    '.MuiPickersSectionList-root'
].join(', ');

/** Inputs a single line of text is typed into — the ones Enter moves on from. */
const TEXT_INPUT_TYPES = new Set([
    'text',
    'search',
    'email',
    'tel',
    'url',
    'number',
    'password'
]);

/**
 * Whether Enter in this element means "done with this field". Read-only ones
 * count too: their Enter must not fall through to the browser, which would
 * submit the form.
 */
export function isSingleLineEntry(target: EventTarget | null): boolean {
    if (target instanceof HTMLInputElement) {
        return TEXT_INPUT_TYPES.has(target.type);
    }
    return (
        target instanceof HTMLElement &&
        target.closest('.MuiPickersSectionList-root') !== null
    );
}

/**
 * Whether this input belongs to an Autocomplete holding ONE value. Enter there
 * has taken the value (or the text) and the form moves on; in a multi-value one
 * it added a chip, and the next chip may follow. MUI marks neither on the
 * input, so: chips already in the field, or an open list that lets several be
 * ticked.
 */
export function isSingleValueAutocomplete(input: HTMLElement): boolean {
    const root = input.closest('.MuiAutocomplete-root');
    if (!root || root.querySelector('.MuiAutocomplete-tag')) {
        return false;
    }
    const listboxId = input.getAttribute('aria-controls');
    const listbox = listboxId ? document.getElementById(listboxId) : null;
    return listbox?.getAttribute('aria-multiselectable') !== 'true';
}

function isEnterable(element: HTMLElement): boolean {
    return (
        element.tabIndex >= 0 &&
        !element.hasAttribute('disabled') &&
        !element.hasAttribute('readonly') &&
        element.getAttribute('aria-hidden') !== 'true' &&
        // Collapsed sections and hidden tabs hold fields Enter must skip.
        // Optional: older engines (and jsdom) lack it and count all as shown.
        (element.checkVisibility?.() ?? true)
    );
}

/**
 * Focus the field after the one `from` belongs to, in document order — what
 * Tab would reach, minus the buttons. Several focusable parts of one field (a
 * date's sections, an Autocomplete's input) count as that one field. Returns
 * whether there was a next field; after the last one Enter stays put.
 */
export function focusNextField(container: HTMLElement, from: HTMLElement) {
    const current = from.closest('.MuiFormControl-root') ?? from;
    const fields = [
        ...container.querySelectorAll<HTMLElement>(FIELD_SELECTOR)
    ].filter(isEnterable);
    const next = fields.find(
        (field) =>
            !current.contains(field) &&
            current.compareDocumentPosition(field) &
                Node.DOCUMENT_POSITION_FOLLOWING
    );
    next?.focus();
    return next !== undefined;
}
